import { useEffect, useMemo, useRef, useState } from 'react';

export interface DropdownOption { value: string; label: string; disabled?: boolean; }

interface Props {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

/** Searchable dropdown used by filter and form fields across ERP. */
export function SearchableDropdown({ value, options, onChange, placeholder = 'Chọn giá trị', searchPlaceholder = 'Tìm kiếm...', disabled = false, className = '', ariaLabel }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selected = options.find((item) => item.value === value);
  const filtered = useMemo(() => options.filter((item) => item.label.toLocaleLowerCase('vi').includes(query.toLocaleLowerCase('vi'))), [options, query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const close = (event: MouseEvent) => { if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    const focus = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => { document.removeEventListener('mousedown', close); window.clearTimeout(focus); };
  }, [open]);

  return (
    <div ref={rootRef} className={`searchable-dropdown ${className}`}>
      <button type="button" className="searchable-dropdown-trigger" aria-label={ariaLabel} aria-expanded={open} disabled={disabled} onClick={() => setOpen((current) => !current)}>
        <span>{selected?.label ?? placeholder}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="searchable-dropdown-popover">
          <div className="searchable-dropdown-search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>
            <input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={searchPlaceholder} />
          </div>
          <div className="searchable-dropdown-options">
            {filtered.map((item) => (
              <button key={item.value} type="button" disabled={item.disabled} className={item.value === value ? 'is-selected' : ''} onClick={() => { onChange(item.value); setOpen(false); }}>
                {item.label}
              </button>
            ))}
            {filtered.length === 0 && <div className="searchable-dropdown-empty">Không tìm thấy kết quả.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
